import { useEffect, useState } from 'react'
import { jobsApi, type MyJobState } from '../api/jobs'

/**
 * F-066 (TASK-0320, REQ-228-002): segue un job lanciato dall'utente senza bloccare la UI. Interroga
 * `GET /api/jobs/mine/{id}` in background, con un intervallo che cresce (backoff) finche' lo stato non
 * cambia e riparte dal minimo quando l'avanzamento si muove; si ferma da solo a lavoro concluso, allo
 * smontaggio del componente o dopo troppi errori consecutivi.
 */
export interface JobProgressOptions {
  /** Intervallo minimo tra due letture, in ms (default 1000). */
  minDelayMs?: number
  /** Intervallo massimo raggiunto dal backoff, in ms (default 8000). */
  maxDelayMs?: number
  /** Errori di fila dopo i quali si smette di insistere (default 5). */
  maxConsecutiveErrors?: number
}

export interface JobProgressView {
  /** Ultimo stato letto; `null` finche' la prima lettura non e' arrivata. */
  state: MyJobState | null
  /** Messaggio se si e' smesso di seguire il job per errori (il job in se' potrebbe essere comunque in corso). */
  error: string | null
  /** `true` quando il job e' concluso (riuscito, fallito o annullato). */
  finished: boolean
}

/** Prossimo intervallo: invariato in ripartenza, altrimenti x1,5 fino al tetto. */
export function nextPollDelay(current: number, progressMoved: boolean, minDelayMs: number, maxDelayMs: number): number {
  if (progressMoved) return minDelayMs
  return Math.min(maxDelayMs, Math.round(current * 1.5))
}

export function useJobProgress(jobId: number | null, options: JobProgressOptions = {}): JobProgressView {
  const minDelayMs = options.minDelayMs ?? 1000
  const maxDelayMs = options.maxDelayMs ?? 8000
  const maxConsecutiveErrors = options.maxConsecutiveErrors ?? 5

  const [view, setView] = useState<JobProgressView>({ state: null, error: null, finished: false })

  useEffect(() => {
    if (jobId === null) {
      setView({ state: null, error: null, finished: false })
      return
    }

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let delay = minDelayMs
    let errors = 0
    let lastDone = -1

    setView({ state: null, error: null, finished: false })

    const poll = async (): Promise<void> => {
      try {
        const state = await jobsApi.mine(jobId)
        if (cancelled) return
        errors = 0
        const moved = (state.done ?? 0) !== lastDone
        lastDone = state.done ?? 0
        setView({ state, error: null, finished: state.finished })
        if (state.finished) return
        delay = nextPollDelay(delay, moved, minDelayMs, maxDelayMs)
      } catch (e) {
        if (cancelled) return
        errors += 1
        if (errors >= maxConsecutiveErrors) {
          setView(v => ({
            ...v,
            error: e instanceof Error ? e.message : 'Impossibile leggere lo stato del job',
          }))
          return
        }
        delay = Math.min(maxDelayMs, delay * 2)
      }
      timer = setTimeout(() => { void poll() }, delay)
    }

    timer = setTimeout(() => { void poll() }, 0)

    return () => {
      cancelled = true
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [jobId, minDelayMs, maxDelayMs, maxConsecutiveErrors])

  return view
}
